import { Injectable } from '@angular/core';
import { Actions, Effect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { State } from 'src/app/store';
import { ClearOrganisationAreasAction, ClearServicesModuleAction, ClearCollectionsModuleAction } from 'src/app/global/store/global.actions';
import { ClearAuthStateAction, ClearUserAction, LogoutAction, SetOrgEntriesAction } from './auth.actions';
import { authConfigIdSelector } from './auth.selectors';
import { OidcOrgsCacheService } from '../service/oidc-orgs-cache.service';

@Injectable()
export class AuthEffects {
    constructor(
        private actions$: Actions,
        private oidcService: OidcSecurityService,
        private orgsCacheService: OidcOrgsCacheService,
        private store: Store<State>,
    ) {}

    /**
     * On every app init, read the OrgEntry list from the cache service
     * (populated by SingleOidcConfigLoader during app startup) and store it
     * in NgRx so the login component can show the city dropdown.
     */
    @Effect()
    loadOrgEntries$ = this.actions$.pipe(
        ofType(ROOT_EFFECTS_INIT),
        switchMap(() =>
            this.orgsCacheService.orgs$.pipe(
                take(1),
                map((orgEntries) => SetOrgEntriesAction({ orgEntries })),
            ),
        ),
    );

    /**
     * Clears FE storage and calls OIDC logoff which revokes tokens and
     * redirects the browser to postLogoutRedirectUri (app root → /login).
     */
    @Effect({ dispatch: false })
    logout$ = this.actions$.pipe(
        ofType(LogoutAction),
        switchMap(() =>
            this.store.select(authConfigIdSelector).pipe(
                take(1),
                switchMap((storeConfigId) => {
                    const configId = storeConfigId || localStorage.getItem('oidc-active-clientId') || undefined;

                    // Set logout flag before logoff — logoff may redirect immediately
                    sessionStorage.setItem('oidc-manual-logout', 'true');

                    const logoffResult = this.oidcService.logoff(configId) as unknown;

                    localStorage.removeItem('oidc-active-clientId');
                    localStorage.removeItem('bciti-auth');
                    sessionStorage.removeItem('oidc-pending-org');

                    const isObservableResult = !!logoffResult && typeof (logoffResult as Observable<unknown>).subscribe === 'function';
                    if (isObservableResult) {
                        return (logoffResult as Observable<unknown>).pipe(catchError(() => of(null)));
                    }

                    return of(logoffResult);
                }),
            ),
        ),
    );

    @Effect()
    logoutClearState$ = this.actions$.pipe(
        ofType(LogoutAction),
        switchMap(() => [ClearAuthStateAction(), ClearServicesModuleAction(), ClearCollectionsModuleAction(), ClearOrganisationAreasAction(), ClearUserAction()]),
    );
}
